import { useCallback, useState } from 'react';
import { MultiStepControl } from '@frontend/design-system';
import { useCareCreditFinanceOptions } from '../../../hooks/use-care-credit-financing-options';
import { useCareCreditLocationProviderStatus } from '../../../hooks/use-care-credit-location-provider-status';
import { FinanceOption } from '../../../types/care-credit.types';
import {
  CareCreditCollectMultiStepBaseProps,
  CollectFormFieldNames,
  CollectFormState,
} from '../collect-multi-step.types';
import { CareCreditCollectContext } from './care-credit-collect.context';

export type CareCreditCollectProviderProps = React.PropsWithChildren<
  Omit<CareCreditCollectMultiStepBaseProps, 'multiStepProps'> & {
    multiStepControl: MultiStepControl;
  }
>;

export const CareCreditCollectProvider = ({
  children,
  locationId,
  merchant,
  person,
  invoice,
  careCreditData,
  hideBackButtonOnCareCreditCollect,
  multiStepControl,
  onCancel,
  onGoBack,
  onGoToMessageStep,
  onSubmissionComplete,
  onRefetchInvoice,
}: CareCreditCollectProviderProps) => {
  const [collectForm, setCollectForm] = useState<CollectFormState>({
    fields: {
      accountHolderName: '',
      purchaseAmount: '',
      serviceCompletionDate: '',
      primaryIdType: '',
      issuingState: '',
      expirationDate: '',
    },
    isValid: false,
  });

  const { merchantNumber } = useCareCreditLocationProviderStatus({ locationId });

  const { isLoading: isLoadingFinanceOptions, financeOptions } = useCareCreditFinanceOptions({
    personId: person?.personId, // or careCreditData.careCreditPersonId
    locationId,
    amount: Number(invoice?.billedAmount) || 0,
    merchantNumber,
  });

  const [selectedFinanceOption, setSelectedFinanceOption] = useState<FinanceOption | undefined>(undefined);

  const isLoading = !person || !careCreditData;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onChangeFormField = useCallback((field: CollectFormFieldNames, value: string, formIsValid: boolean) => {
    setCollectForm((prev) => ({
      fields: {
        ...prev.fields,
        [field]: value,
      },
      isValid: formIsValid,
    }));
  }, []);

  return (
    <CareCreditCollectContext.Provider
      value={{
        // forwarded props
        locationId,
        merchant,
        person,
        invoice,
        careCreditData,
        hideBackButtonOnCareCreditCollect,
        multiStep: multiStepControl,
        onCancel,
        onGoBack,
        onGoToMessageStep,
        onRefetchInvoice,
        onSubmissionComplete,
        // internal state
        isLoading,
        isSubmitting,
        collectForm,
        financeOptions,
        isLoadingFinanceOptions,
        selectedFinanceOption,
        setIsSubmitting,
        onChangeFormField,
        setSelectedFinanceOption,
      }}
    >
      {children}
    </CareCreditCollectContext.Provider>
  );
};
